import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, ShieldCheck } from "lucide-react";
import { track } from "../lib/tracking";

const STORAGE_KEY = "sdc_exit_intent_shown_v1";

/**
 * Single-shot exit-intent modal. Fires when:
 *   - cursor leaves the viewport via the top edge, OR
 *   - user has spent >45s on the page and switches tabs (visibilitychange).
 * Suppressed after one display per browser (localStorage).
 * Compliance-safe: no outcome promises; offers a free checklist + eligibility check.
 */
export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);

  const close = useCallback(() => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (err) {
      // localStorage may be disabled (Safari private mode, embedded webview).
      // Failing silently here is intentional — the modal still closes for the
      // user; we just lose the "don't show again" suppression for this session.
      console.warn("ExitIntentModal: localStorage.setItem failed:", err);
    }
  }, []);

  useEffect(() => {
    let alreadyShown = false;
    try {
      alreadyShown = localStorage.getItem(STORAGE_KEY) === "1";
    } catch (err) {
      // Same rationale as above. If we can't read the flag, default to
      // showing the modal (the worst case is showing it once per session).
      console.warn("ExitIntentModal: localStorage.getItem failed:", err);
    }
    if (alreadyShown) return undefined;

    const startedAt = Date.now();
    let fired = false;

    const fire = (trigger) => {
      if (fired) return;
      fired = true;
      setOpen(true);
      track("ExitIntentModalShown", {
        trigger,
        seconds_on_page: Math.round((Date.now() - startedAt) / 1000),
      });
    };

    const onMouseLeave = (e) => {
      // Only fire on top-edge exits (typical "leaving" gesture)
      if (e.clientY <= 0) fire("cursor_top");
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        const elapsed = (Date.now() - startedAt) / 1000;
        if (elapsed > 45) fire("tab_hidden");
      }
    };

    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="exit-intent-title"
      data-testid="exit-intent-modal"
    >
      <div className="relative w-full max-w-lg rounded-2xl border border-blue-500/30 bg-slate-900 p-8 shadow-2xl">
        <button
          type="button"
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-white/10 hover:text-white transition"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/20 text-blue-300 text-xs font-medium">
          <ShieldCheck className="w-4 h-4" />
          Before you go
        </div>

        <h2
          id="exit-intent-title"
          className="text-2xl md:text-3xl font-bold text-white leading-tight"
        >
          Don't appeal blind.
        </h2>
        <p className="mt-4 text-slate-300 leading-relaxed">
          Most appeals fail because of one of <strong className="text-white">seven specific mistakes</strong>.
          Read our free checklist before you submit — it takes 4 minutes and could save your account.
        </p>

        <div className="mt-8 flex flex-col gap-3">
          <Link
            to="/blog/worst-mistakes-social-media-account-appeal"
            onClick={() => {
              track("ExitIntentModalCTA", { action: "read_checklist" });
              close();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3 font-semibold text-white hover:bg-blue-600 transition"
            data-testid="exit-intent-cta-primary"
          >
            Read the 7-Mistake Checklist
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/contact"
            onClick={() => {
              track("ExitIntentModalCTA", { action: "eligibility_check" });
              close();
            }}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 px-6 py-3 font-medium text-white hover:bg-white/5 transition"
            data-testid="exit-intent-cta-secondary"
          >
            Or check if my case qualifies →
          </Link>
        </div>

        <p className="mt-4 text-xs text-slate-500 text-center">
          Free. No payment required. Outcomes are determined by each platform.
        </p>
      </div>
    </div>
  );
}
